import { useState } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, MessageSquare, Clock, Send, Check, HelpCircle, Bug, Lightbulb } from "lucide-react";
import { SEO } from "@/components/SEO";

const Support = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Eroare la trimiterea mesajului. Te rog încearcă din nou sau contactează-ne direct la contactoptiro@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <PageLayout>
      <SEO 
        title="Suport"
        description="Contactează echipa OptiRO pentru asistență, întrebări sau feedback. Răspundem în maximum 24 ore."
        canonical="https://optiro.ai/support"
      />
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">Suport OptiRO.dev</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Suntem aici să te ajutăm. Trimite-ne un mesaj și îți vom răspunde cât mai curând.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Email</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  <a href="mailto:contactoptiro@gmail.com" className="text-primary hover:underline">
                    contactoptiro@gmail.com
                  </a>
                </CardDescription>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Timp de răspuns</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Răspundem în maximum 24 de ore în zilele lucrătoare (L-V, 9:00-18:00).
                </CardDescription>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Resurse rapide</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <a href="/faq" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <HelpCircle className="w-4 h-4" />
                  Întrebări frecvente
                </a>
                <a href="/blog" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Lightbulb className="w-4 h-4" />
                  Articole și ghiduri
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Trimite-ne un mesaj</CardTitle>
              <CardDescription>
                Completează formularul și te vom contacta în cel mai scurt timp.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mesajul tău a fost trimis!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Îți vom răspunde la adresa {formData.email} în maximum 24 de ore.
                  </p>
                  <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                    Trimite alt mesaj
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nume complet</Label>
                      <Input
                        id="name"
                        placeholder="Ion Popescu"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Adresă email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ion@exemplu.ro"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Categorie</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => handleInputChange("category", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selectează categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">
                            <span className="flex items-center gap-2">
                              <HelpCircle className="w-4 h-4" />
                              Întrebare generală
                            </span>
                          </SelectItem>
                          <SelectItem value="technical">
                            <span className="flex items-center gap-2">
                              <Bug className="w-4 h-4" />
                              Problemă tehnică
                            </span>
                          </SelectItem>
                          <SelectItem value="feedback">
                            <span className="flex items-center gap-2">
                              <Lightbulb className="w-4 h-4" />
                              Feedback / Sugestii
                            </span>
                          </SelectItem>
                          <SelectItem value="partnership">
                            <span className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Parteneriate
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subiect</Label>
                      <Input
                        id="subject"
                        placeholder="Despre ce este mesajul?"
                        value={formData.subject}
                        onChange={(e) => handleInputChange("subject", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mesaj</Label>
                    <Textarea
                      id="message"
                      placeholder="Descrie în detaliu întrebarea sau problema ta..."
                      className="min-h-[150px]"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Se trimite...' : 'Trimite mesajul'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default Support;
